import {
    VIRTUAL_APPOINTMENT_TEAMS,
    type VirtualAppointmentTeam
} from '../../services/virtualAppointments';
import type {
    PresentationSelectOption,
    PresentationSelectPlacement
} from '../events/PresentationSelect';
import { PresentationSelect } from '../events/PresentationSelect';
import { getTeamColor } from '@odo/shared-ui/components/teamColors';

interface TeamDotProps {
    color: string;
    placement: PresentationSelectPlacement;
}

// On the filled trigger the dot fades to white, like the status dropdown; in the list it wears the colour.
function TeamDot({ color, placement }: TeamDotProps) {
    const onTrigger = placement === 'trigger';
    return (
        <span
            aria-hidden="true"
            className={`w-2 h-2 rounded-full flex-shrink-0 ${onTrigger ? 'bg-white/40' : ''}`}
            style={onTrigger ? undefined : { backgroundColor: color }}
        />
    );
}

// Filled with the team's TeamPill colour, the same way the status dropdown wears its status colour.
const TEAM_OPTIONS: readonly PresentationSelectOption<VirtualAppointmentTeam>[] =
    VIRTUAL_APPOINTMENT_TEAMS.map((team) => {
        const color = getTeamColor(team);
        return {
            value: team,
            label: team,
            render: (placement) => <TeamDot color={color} placement={placement} />,
            triggerTone: {
                trigger:
                    'border-transparent text-white font-semibold hover:brightness-95 disabled:hover:brightness-100',
                chevron: 'text-white/80',
                style: { backgroundColor: color }
            }
        };
    });

interface VirtualAppointmentTeamDropdownProps {
    id: string;
    value: VirtualAppointmentTeam;
    onChange: (team: VirtualAppointmentTeam) => void;
    disabled?: boolean;
}

export function VirtualAppointmentTeamDropdown({
    id,
    value,
    onChange,
    disabled = false
}: VirtualAppointmentTeamDropdownProps) {
    const select = (next: VirtualAppointmentTeam) => {
        if (next !== value) onChange(next);
    };
    return (
        <PresentationSelect
            id={id}
            value={value}
            options={TEAM_OPTIONS}
            onChange={select}
            disabled={disabled}
            size="sm"
        />
    );
}
